import React, { useState } from 'react';
import { App, Button, Spin, Tag, Typography, theme } from 'antd';
import { CheckCircleFilled, EditOutlined, SyncOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import SignatureDrawer from './SignatureDrawer';
import { useFirmaPreview } from './useFirmaPreview';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { saveSignature } from 'utils/offlineSignaturesStore';
import { presignSignatureUpload } from 'api/signatures';
import './Signature.scss';

const { Text } = Typography;

/**
 * Persiste el blob de una firma y devuelve la referencia agnóstica con la
 * que se guarda el campo del formulario:
 *  - offline → localId (uuid) — el blob queda en IndexedDB, el daemon de
 *    reintento lo sube a S3 al reconectar.
 *  - online  → S3 key (`signatures/...`), tras presign + PUT directo a S3.
 *
 * Extraída como función pura (sin hooks) para poder testearla sin depender
 * de la interacción con el canvas, que no es reproducible de forma fiable
 * en jsdom.
 *
 * @param {object} params
 * @param {Blob} params.blob - PNG de la firma dibujada.
 * @param {boolean} params.isOnline - estado de conexión al momento de guardar.
 * @param {number|string} params.mantencionId - mantención a la que pertenece la firma.
 * @param {'spec'|'recep'} params.role - quién firma.
 * @returns {Promise<string>} localId (offline) o S3 key (online).
 */
export async function persistirFirma({ blob, isOnline, mantencionId, role }) {
  if (!isOnline) {
    const localId = uuidv4();
    await saveSignature(localId, { blob, mantencionId, role });
    return localId;
  }
  const { uploadUrl, key } = await presignSignatureUpload();
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/png' },
    body: blob,
  });
  if (!putRes || !putRes.ok) {
    throw new Error(`S3 PUT firma falló: ${putRes && putRes.status}`);
  }
  return key;
}

const TITULOS = {
  spec: 'Firma del especialista',
  recep: 'Firma del recepcionista',
};

/**
 * Modo de guardado según la conexión. Solo el cierre de mantención sabe subir
 * después una firma capturada sin señal (`offlineCierreSync` la resuelve por
 * `mantencionId`); en los demás formularios un `localId` quedaría como un uuid
 * inservible en el payload, así que ahí se exige conexión.
 */
export function modoDeGuardado({ isOnline, mantencionId }) {
  if (isOnline) return 'online';
  return mantencionId != null ? 'queue' : 'blocked';
}

/**
 * Campo de firma controlado (compatible con antd Form.Item: recibe `value` y
 * `onChange` directamente, así que debe ser el hijo directo del Form.Item).
 *
 * Estados:
 *  - sin valor   → botón "Toque aquí para firmar"
 *  - con valor   → preview de la firma + "Cambiar firma" (y "Pendiente de
 *                  sincronizar" si se capturó sin conexión)
 *  - disabled    → solo preview, sin acciones
 *
 * La captura ocurre en un Drawer (ver `SignatureDrawer`). Al guardar:
 *  1. el PNG recortado se persiste con `persistirFirma`
 *  2. `onChange(ref)` recibe la S3 key (online) o el localId (offline)
 *
 * @param {object} props
 * @param {string} [props.value] - S3 key, localId o URL.
 * @param {(ref: string) => void} [props.onChange]
 * @param {boolean} [props.disabled]
 * @param {'spec'|'recep'} [props.role] - define el título del Drawer y se guarda con la firma offline.
 * @param {number|string} [props.mantencionId] - habilita capturar sin conexión (cierre de mantención).
 * @param {string} [props.label] - título del Drawer; por defecto sale del `role`.
 */
function Signature({ value, onChange, disabled = false, mantencionId, role, label }) {
  const { notification } = App.useApp();
  const { token } = theme.useToken();
  const isOnline = useOnlineStatus();
  const [open, setOpen] = useState(false);
  const { url, loading, pendiente, firmada } = useFirmaPreview(value);

  const titulo = label ?? TITULOS[role] ?? 'Firma';
  const modo = modoDeGuardado({ isOnline, mantencionId });
  const vars = { '--firma-borde': token.colorBorder, '--firma-radio': `${token.borderRadiusLG}px` };

  const handleSave = async (blob) => {
    try {
      const ref = await persistirFirma({ blob, isOnline, mantencionId, role });
      onChange?.(ref);
      setOpen(false);
    } catch (err) {
      notification.error({ message: 'Error guardando la firma', description: err.message });
    }
  };

  const imagen = loading ? (
    <Spin size="small" />
  ) : url ? (
    <img src={url} alt="Firma" />
  ) : (
    <Text type="secondary">Firma registrada</Text>
  );

  // Modo lectura (post-guardado del record padre)
  if (disabled) {
    if (!firmada) return <Text type="secondary">Sin firma</Text>;
    return (
      <div className="firma-campo" style={vars}>
        <div className="firma-campo__firmada">
          <div className="firma-campo__imagen">{imagen}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="firma-campo" style={vars}>
      {firmada ? (
        <div className="firma-campo__firmada">
          <div className="firma-campo__imagen">{imagen}</div>
          <div className="firma-campo__info">
            <Text style={{ color: token.colorSuccess }}>
              <CheckCircleFilled /> Firmada
            </Text>
            {pendiente && (
              <Tag icon={<SyncOutlined />} color="warning">
                Pendiente de sincronizar
              </Tag>
            )}
            <Button size="small" icon={<EditOutlined />} onClick={() => setOpen(true)}>
              Cambiar firma
            </Button>
          </div>
        </div>
      ) : (
        <Button
          block
          type="dashed"
          icon={<EditOutlined />}
          onClick={() => setOpen(true)}
          className="firma-campo__vacio"
        >
          Toque aquí para firmar
        </Button>
      )}

      <SignatureDrawer
        open={open}
        title={titulo}
        modo={modo}
        onSave={handleSave}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}

export default Signature;
